// 非牌桌畫面共用樣式：奶油粗框、暖白文字與 2D 霧面按鈕。
// === 使用圖片 ===
// - 面板與按鈕間接使用 dialog-skin 的奶油插畫素材，詳見該檔頭。
// - 標題飾線使用 Phaser.Graphics / Phaser.Text，無直接圖片。

import type Phaser from 'phaser';
import {
  createDialogPanel,
  createDialogButtonSurface,
  type CDialogButtonSurface,
} from './dialog-skin';

export const PAGE_THEME = {
  gold: 0xe9d9ac,
  goldText: '#fff1cf',
  text: '#fff5d6',
  muted: '#c4d7c7',
  font: '"PingFang TC", "Microsoft JhengHei", sans-serif',
  panel: 0x0a382a,
  row: 0x12503a,
  selected: 0x326342,
  disabled: 0x293e33,
  negative: '#f1afa0',
} as const;

const TITLE_FONT_SIZE = 38; // 頁面標題字級
const TITLE_LINE_OFFSET = 150; // 標題兩側飾線起點
const TITLE_LINE_LENGTH = 88; // 飾線長度
const TITLE_LINE_WIDTH = 1; // 飾線線寬
const TITLE_DIAMOND_SIZE = 4; // 飾線端點菱形半徑
const BUTTON_TEXT_STROKE = '#063427'; // 保留文字色彩欄位
const BUTTON_STROKE_WIDTH = 0; // 文字描邊寬
const BUTTON_FONT_SIZE = 28; // 共用霧面按鈕字級
const BUTTON_DISABLED_ALPHA = 0.45; // 停用按鈕透明度

export function createPagePanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number
): Phaser.GameObjects.Container {
  return createDialogPanel(scene, x, y, width, height, false);
}

export function createPageTitle(scene: Phaser.Scene, x: number, y: number, title: string): void {
  scene.add
    .text(x, y, title, {
      fontFamily: PAGE_THEME.font,
      fontSize: `${TITLE_FONT_SIZE}px`,
      fontStyle: 'bold',
      color: PAGE_THEME.text,
    })
    .setOrigin(0.5);
  const g = scene.add.graphics().lineStyle(TITLE_LINE_WIDTH, PAGE_THEME.gold);
  for (const direction of [-1, 1]) {
    const start = x + TITLE_LINE_OFFSET * direction;
    const end = start + TITLE_LINE_LENGTH * direction;
    g.lineBetween(start, y, end, y);
    g.beginPath();
    g.moveTo(end, y - TITLE_DIAMOND_SIZE);
    g.lineTo(end + TITLE_DIAMOND_SIZE, y);
    g.lineTo(end, y + TITLE_DIAMOND_SIZE);
    g.lineTo(end - TITLE_DIAMOND_SIZE, y);
    g.closePath();
    g.strokePath();
  }
}

export function createPageButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  label: string,
  enabled = true
): CDialogButtonSurface {
  const surface = createDialogButtonSurface(scene, x, y, width, height);
  surface.add(
    scene.add
      .text(0, 0, label, {
        fontFamily: PAGE_THEME.font,
        fontSize: `${BUTTON_FONT_SIZE}px`,
        stroke: BUTTON_TEXT_STROKE,
        strokeThickness: BUTTON_STROKE_WIDTH,
        fontStyle: 'bold',
        color: PAGE_THEME.text,
      })
      .setOrigin(0.5)
  );
  surface.setAlpha(enabled ? 1 : BUTTON_DISABLED_ALPHA);
  return surface;
}
